import React, { useEffect, useRef } from 'react';

import {
  CLIENT_STATS_POLL_INTERVAL_MS,
  CLIENT_STATS_START_GRACE_MS,
  CLIENT_STATS_WINDOW_MS,
} from '@/constants';
import { useWebRtcStatsStore } from '@/stores';

import {
  addPacketCountSample,
  getWindowCountDelta,
  getWindowNackPercent,
  getWindowPacketLossPercent,
  isWithinStartGrace,
  type PacketCountSample,
} from './clientPacketLoss.functions.ts';
import { resolveTransport } from './resolveTransport';

interface UseWebRtcStatsOptions {
  pcRef: React.RefObject<RTCPeerConnection | null>;
  connectionState: RTCPeerConnectionState;
  streamUri: string | undefined;
  onFramesDecoded?: (framesDecoded: number) => void;
}

export const useWebRtcStats = ({
  pcRef,
  connectionState,
  streamUri,
  onFramesDecoded,
}: UseWebRtcStatsOptions) => {
  const setStats = useWebRtcStatsStore(s => s.setStats);
  const removeStats = useWebRtcStatsStore(s => s.removeStats);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Polls inside the loss window, so loss is judged over the same span as the source's.
  const samplesRef = useRef<PacketCountSample[]>([]);
  // When this connection came up; the start-up burst of PLIs and NACKs is not sampled.
  const connectedAtRef = useRef<number | null>(null);

  useEffect(() => {
    const pc = pcRef.current;
    if (!pc || connectionState !== 'connected') {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      samplesRef.current = [];
      connectedAtRef.current = null;
      return;
    }
    connectedAtRef.current ??= performance.now();
    const connectedAtMs = connectedAtRef.current;

    let cancelled = false;
    const poll = async () => {
      try {
        const stats = await pc.getStats();
        // A poll that finishes after cleanup must not report on a connection that has moved on.
        if (cancelled) return;
        const transport = resolveTransport(stats);
        stats.forEach(report => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const jitterMs = (report.jitter ?? 0) * 1000;
            const nowMs = performance.now();
            // The first sample after the grace becomes the baseline, so the burst falls outside.
            if (
              !isWithinStartGrace({ connectedAtMs, nowMs, graceMs: CLIENT_STATS_START_GRACE_MS })
            ) {
              samplesRef.current = addPacketCountSample({
                samples: samplesRef.current,
                sample: {
                  atMs: nowMs,
                  packetsLost: report.packetsLost ?? 0,
                  packetsReceived: report.packetsReceived ?? 0,
                  nackCount: report.nackCount,
                  pliCount: report.pliCount,
                },
                windowMs: CLIENT_STATS_WINDOW_MS,
              });
            }
            const packetLossPercent = getWindowPacketLossPercent(samplesRef.current);

            if (streamUri) {
              setStats(streamUri, {
                clientJitterMs: jitterMs,
                clientPacketLossPercent: packetLossPercent,
                clientNackPercent: getWindowNackPercent(samplesRef.current),
                clientPliCount: getWindowCountDelta({
                  samples: samplesRef.current,
                  select: s => s.pliCount,
                }),
                transport,
              });
            }
            onFramesDecoded?.(report.framesDecoded ?? 0);
          }
        });
      } catch {
        // Connection may have closed between check and getStats call
      }
    };

    poll();
    intervalRef.current = setInterval(poll, CLIENT_STATS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pcRef, connectionState, streamUri, setStats, onFramesDecoded]);

  useEffect(() => {
    return () => {
      if (streamUri) {
        removeStats(streamUri);
      }
    };
  }, [streamUri, removeStats]);
};
